export interface Category {
  id: string;
  name: string;
  created_at?: string;
}

export interface PaymentMethod {
  id: string;
  name: string;
  enabled: boolean;
}

export interface Product {
  id: string;
  name: string;
  sku: string | null;
  barcode: string | null;
  brand: string | null;
  description: string | null;
  category_id: string | null;
  purchase_price: number | null;
  sale_price: number;
  wholesale_price: number | null;
  tax_percentage: number | null;
  stock: number;
  min_stock: number | null;
  image_url: string | null;
  show_in_catalog: boolean;
  active: boolean;
  wholesale_enabled: boolean;
  created_at?: string;
  updated_at?: string;
  category?: Category | null;
}

export interface Client {
  id: string;
  name: string;
  phone: string | null;
  email: string | null;
  rnc: string | null;
  address: string | null;
  company: string | null;
  contact_name: string | null;
  is_wholesale: boolean;
  wholesale_code: string | null;
  active: boolean;
  created_at?: string;
}

export interface Sale {
  id: string;
  invoice_number: string;
  cashier_id: string | null;
  cashier_name: string | null;
  customer_id: string | null;
  customer_name: string | null;
  subtotal: number;
  discount: number;
  tax: number;
  total: number;
  payment_method: string;
  amount_received: number | null;
  change_amount: number | null;
  status: string;
  notes: string | null;
  discount_code: string | null;
  discount_code_id: string | null;
  discount_type: string | null;
  discount_code_amount: number | null;
  refund_type: string | null;
  refund_total: number | null;
  refunded_at: string | null;
  refunded_by: string | null;
  refunded_by_name: string | null;
  void_reason: string | null;
  voided_at: string | null;
  voided_by: string | null;
  voided_by_name: string | null;
  branch_id?: string | null;
  branch?: {
    name: string;
    legal_name?: string | null;
    rnc?: string | null;
    logo_url?: string | null;
    address?: string | null;
    phone?: string | null;
    whatsapp?: string | null;
    email?: string | null;
    description?: string | null;
  } | null;
  created_at?: string;
}

export interface SaleItem {
  id: string;
  sale_id: string;
  product_id: string | null;
  product_name: string;
  quantity: number;
  unit_price: number;
  discount: number | null;
  subtotal: number;
  original_price: number | null;
  discount_percentage: number | null;
  discount_type: string | null;
  is_wholesale: boolean;
  unit_cost?: number | null;
  returned_quantity?: number | null;
  discount_applied_by_name?: string | null;
  discount_authorized_by_name?: string | null;
  created_at?: string;
}

// Estado lógico de una factura (nunca se borra físicamente).
export type InvoiceStatus =
  | 'completed'
  | 'partial_refund'
  | 'refunded'
  | 'voided'
  | 'deleted';

export const INVOICE_STATUS_META: Record<
  string,
  { label: string; badge: string }
> = {
  completed: { label: 'ACTIVA', badge: 'bg-accent-100 text-accent-900' },
  partial_refund: { label: 'REEMBOLSO PARCIAL', badge: 'bg-secondary-100 text-secondary-900' },
  refunded: { label: 'REEMBOLSADA', badge: 'bg-primary-100 text-primary-800' },
  voided: { label: 'ANULADA', badge: 'bg-background-200 text-foreground-700' },
  deleted: { label: 'ELIMINADA', badge: 'bg-background-200 text-foreground-600' },
};

export function invoiceStatusMeta(status: string | null | undefined) {
  return (
    INVOICE_STATUS_META[status ?? 'completed'] ?? {
      label: status ?? '—',
      badge: 'bg-background-200 text-foreground-700',
    }
  );
}

export type InvoiceActionType = 'refund_full' | 'refund_partial' | 'void' | 'delete';

export const INVOICE_ACTION_LABELS: Record<string, string> = {
  refund_full: 'Reembolso total',
  refund_partial: 'Reembolso parcial',
  void: 'Anulación',
  delete: 'Eliminación',
};

export interface InvoiceAction {
  id: string;
  sale_id: string | null;
  invoice_number: string | null;
  branch_id: string | null;
  action: InvoiceActionType | string;
  amount: number;
  items: { product_name: string; quantity: number; amount: number }[] | null;
  reason: string | null;
  user_id: string | null;
  user_name: string | null;
  created_at: string;
}

export type MovementType = 'entrada' | 'salida' | 'venta' | 'ajuste' | 'devolucion';

export type CashSessionStatus = 'open' | 'closed';
export type CashMovementType = 'entrada' | 'retiro' | 'devolucion';

export interface PaymentBreakdownRow {
  method: string;
  label: string;
  count: number;
  total: number;
}

export interface CashSession {
  id: string;
  user_id: string;
  user_name: string | null;
  user_role: string | null;
  branch_id: string | null;
  period_date: string;
  status: CashSessionStatus;
  opened_at: string;
  closed_at: string | null;
  sales_count: number;
  subtotal: number;
  discount: number;
  tax: number;
  total: number;
  returns_total: number;
  cash_sales: number;
  cash_in: number;
  cash_out: number;
  cash_expected: number;
  cash_counted: number | null;
  difference: number | null;
  revenue: number | null;
  cost_total: number | null;
  profit: number | null;
  margin: number | null;
  payment_breakdown: PaymentBreakdownRow[];
  observations: string | null;
  branch?: { name: string } | null;
  profile?: { role: string; first_name: string | null; last_name: string | null } | null;
  created_at?: string;
  updated_at?: string;
}

export interface CashMovement {
  id: string;
  user_id: string;
  branch_id: string | null;
  type: CashMovementType;
  amount: number;
  note: string | null;
  created_at: string;
}

export interface InventoryMovement {
  id: string;
  product_id: string;
  type: MovementType;
  quantity: number;
  previous_stock: number;
  new_stock: number;
  reason: string | null;
  note: string | null;
  user_id: string | null;
  user_name: string | null;
  sale_id: string | null;
  branch_id?: string | null;
  branch?: { name: string } | null;
  created_at?: string;
  product?: { name: string; image_url: string | null } | null;
}

export interface BusinessInfo {
  id: string;
  name: string;
  logo_url: string | null;
  address: string | null;
  phone: string | null;
  rnc: string | null;
  whatsapp: string | null;
  footer_message: string | null;
  currency_symbol: string;
  instagram_url?: string | null;
  facebook_url?: string | null;
  tiktok_url?: string | null;
  created_at?: string;
  updated_at?: string;
}

export interface CartItem {
  product: Product;
  quantity: number;
  discount: number;
  manualDiscount?: ManualDiscount | null;
}

export interface ManualDiscount {
  type: 'percent' | 'fixed';
  value: number;
  appliedByName: string | null;
  authorizedByName: string | null;
}

export interface ProductDiscount {
  id: string;
  product_id: string;
  percentage: number;
  starts_at: string | null;
  ends_at: string | null;
  active: boolean;
  created_by: string | null;
  created_at?: string;
  updated_at?: string;
  product?: { name: string; sale_price: number } | null;
}

export type DiscountScope = 'cashier' | 'admin';

export interface DiscountCode {
  id: string;
  scope: DiscountScope;
  code: string;
  percentage: number;
  description: string | null;
  starts_at: string | null;
  ends_at: string | null;
  usage_limit: number | null;
  active: boolean;
  authorized_cashier_id: string | null;
  created_by: string | null;
  created_at?: string;
  updated_at?: string;
  authorized_cashier?: { first_name: string | null; last_name: string | null } | null;
  usage_count?: number;
}

export interface DiscountCodeUsage {
  id: string;
  code_id: string;
  sale_id: string | null;
  used_by: string | null;
  used_by_role: string | null;
  amount_discounted: number;
  created_at?: string;
}

export interface WholesaleTier {
  id: string;
  product_id: string;
  min_quantity: number;
  price: number;
  created_at?: string;
}

export interface Offer {
  id: string;
  name: string;
  percentage: number;
  starts_at: string;
  ends_at: string;
  active: boolean;
  created_by?: string | null;
  created_at?: string;
  updated_at?: string;
}

export interface OfferProduct {
  id: string;
  offer_id: string;
  product_id: string;
  created_at?: string;
}

export type OfferEventType =
  | 'created'
  | 'updated'
  | 'product_added'
  | 'product_removed'
  | 'activated'
  | 'finalized'
  | 'cancelled';

export interface OfferEvent {
  id: string;
  offer_id: string | null;
  event_type: OfferEventType;
  user_id: string | null;
  user_name: string | null;
  details: Record<string, unknown> | null;
  created_at: string;
}

export type OfferStatus = 'programada' | 'activa' | 'finalizada';

export interface LinePricing {
  unitPrice: number;
  discountAmount: number;
  discountPct: number | null;
  discountType: string | null;
  isWholesale: boolean;
  offerId?: string | null;
  offerName?: string | null;
  autoDiscount: number;
  autoDiscountPct: number | null;
  autoDiscountType: string | null;
  manualDiscount: number;
  manualDiscountPct: number | null;
  manualDiscountType: 'percent' | 'fixed' | null;
  manualDiscountValue: number;
  manualAuthorizedByName: string | null;
}

export const MOVEMENT_LABELS: Record<string, string> = {
  entrada: 'Entrada',
  salida: 'Salida',
  venta: 'Venta',
  ajuste: 'Ajuste',
  devolucion: 'Devolución',
};

export const PAYMENT_LABELS: Record<string, string> = {
  cash: 'Efectivo',
  card: 'Tarjeta',
  transfer: 'Transferencia',
  mixed: 'Pago mixto',
  other: 'Otro',
};

export function paymentLabel(method: string | null | undefined): string {
  if (!method) return '—';
  return PAYMENT_LABELS[method] || method;
}

/**
 * Parte en efectivo realmente aplicada a la venta.
 * En un pago mixto guardamos en `amount_received` el efectivo entregado y en
 * `change_amount` el cambio devuelto, así que la porción en efectivo es la resta.
 */
export function saleCashPortion(sale: {
  payment_method?: string | null;
  total?: number | null;
  amount_received?: number | null;
  change_amount?: number | null;
}): number {
  if (sale.payment_method === 'cash') return Number(sale.total || 0);
  if (sale.payment_method !== 'mixed') return 0;
  const received = Number(sale.amount_received || 0);
  const change = Number(sale.change_amount || 0);
  const applied = Math.max(0, received - change);
  return Math.max(0, Math.min(Number(sale.total || 0), applied));
}

/** Parte cobrada con tarjeta en un pago mixto. */
export function saleCardPortion(sale: {
  payment_method?: string | null;
  total?: number | null;
  amount_received?: number | null;
  change_amount?: number | null;
}): number {
  if (sale.payment_method !== 'mixed') return 0;
  const total = Number(sale.total || 0);
  return Math.max(0, Math.round((total - saleCashPortion(sale)) * 100) / 100);
}

export interface Branch {
  id: string;
  name: string;
  code: string | null;
  address: string | null;
  phone: string | null;
  whatsapp: string | null;
  rnc: string | null;
  legal_name: string | null;
  email: string | null;
  website: string | null;
  description: string | null;
  city: string | null;
  province: string | null;
  country: string | null;
  latitude: number | null;
  longitude: number | null;
  active: boolean;
  logo_url: string | null;
  instagram_url: string | null;
  facebook_url: string | null;
  tiktok_url: string | null;
  created_at?: string;
  updated_at?: string;
}

export interface CashierProfile {
  id: string;
  role: string;
  first_name: string | null;
  last_name: string | null;
  username: string | null;
  email: string | null;
  phone: string | null;
  avatar_url: string | null;
  branch_id: string | null;
  blocked: boolean;
  code_generated_at?: string | null;
  created_at?: string;
}

export type StockStatus = 'en_stock' | 'bajo' | 'agotado';

export function getStockStatus(stock: number, minStock: number | null | undefined): StockStatus {
  if (!stock || stock <= 0) return 'agotado';
  if (stock <= (minStock ?? 0)) return 'bajo';
  return 'en_stock';
}

export const STOCK_STATUS_META: Record<
  StockStatus,
  { label: string; icon: string; badge: string; dot: string }
> = {
  en_stock: {
    label: 'En stock',
    icon: 'ri-checkbox-circle-fill',
    badge: 'bg-accent-100 text-accent-900',
    dot: 'text-accent-500',
  },
  bajo: {
    label: 'Stock bajo',
    icon: 'ri-alert-fill',
    badge: 'bg-secondary-100 text-secondary-900',
    dot: 'text-secondary-500',
  },
  agotado: {
    label: 'Agotado',
    icon: 'ri-close-circle-fill',
    badge: 'bg-primary-100 text-primary-800',
    dot: 'text-primary-500',
  },
};

export interface ImportRow {
  name: string;
  sku: string;
  barcode: string;
  description: string;
  category_name: string;
  brand: string;
  purchase_price: number | null;
  sale_price: number;
  tax_percentage: number;
  stock: number;
  min_stock: number;
  show_in_catalog: boolean;
  row: number;
}